import { Canceler } from "axios";
import { EntityApi } from "../../services/ApiPlatform";
import { FinalResponse, ListResponse } from "../../services/ApiPlatform/models";
import { PrimitiveObject } from "../../services/ApiPlatform/types";

export abstract class UserApi extends EntityApi {
    protected static GET_COLLECTION = "/api/users";

    protected static POST_COLLECTION = "/api/users";

    protected static GET_ITEM = "/api/users/{id}";

    protected static PUT_ITEM = "/api/users/{id}";

    protected static PATCH_ITEM = "/api/users/{id}";

    protected static DELETE_ITEM = "/api/users/{id}";

    protected static POST_USER_EXIST = "/user-exist";

    protected static POST_USER_CHANGE_PASSWORD_REQUEST =
        "/user-change-password-request";

    protected static POST_USER_SET_PASSWORD = "/user-set-password";

    protected static PATCH_USER_ITEM_PROFILE = "/api/users/{id}/profile";

    protected static PATCH_USER_ITEM_CHANGE_EMAIL =
        "/api/users/{id}/change-email-request";

    protected static PUBLIC_GET_COLLECTION = "/public-api/users/get";

    protected static POST_USER_IMPERSONATE_REQUEST =
        "/api/users/impersonate-request";

    protected static POST_USER_IMPORT_REQUEST = "/api/users/import";

    protected static POST_USER_EXPORT_REQUEST = "/api/users/export";

    protected static GET_COLLECTION_COORDINATOR = "/api/users/coordinator";

    protected static GET_COLLECTION_SCHEDULE = "/api/users/get-for-schedule";

    protected static GET_COLLECTION_FOR_ASSIGN = "/api/users/get-for-assign";

    protected static GET_COLLECTION_ASSIGN = "/api/users/get-assign";

    protected static GET_COLLECTION_USER_MESSAGE = "/api/user-messages";

    protected static GET_ITEM_USER_MESSAGE_COMPLETE = "/api/user-messages/{id}";

    protected static POST_DOWNLOAD = "/api/downloads/{type}/{id}";

    protected static GET_ITEM_STUDENT = "/api/users/{id}/student";

    public static async publicGetCollection<R>(
        page = 1,
        extraParams = {},
        cancelToken?: (c: Canceler) => void
    ): Promise<FinalResponse<ListResponse<R> | null>> {
        extraParams = { ...extraParams, PATH: this.PUBLIC_GET_COLLECTION };
        return this.getCollection(page, extraParams, cancelToken);
    }

    public static async postUserExist<R, P>(
        entity: P,
        extraParams = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = { ...extraParams, PATH: this.POST_USER_EXIST };
        return this.postCollection(entity, extraParams);
    }

    public static async postUserChangePasswordRequest<R, P>(
        entity: P,
        extraParams = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.POST_USER_CHANGE_PASSWORD_REQUEST,
        };
        return this.postCollection(entity, extraParams);
    }

    public static async postUserSetPassword<R, P>(
        entity: P,
        extraParams = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.POST_USER_SET_PASSWORD,
        };
        return this.postCollection(entity, extraParams);
    }

    public static async patchItemProfile<R, P>(
        id: number | string,
        entity: P,
        extraParams: PrimitiveObject = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = { ...extraParams, PATH: this.PATCH_USER_ITEM_PROFILE };
        return this.patchItem(id, entity, extraParams);
    }

    public static async patchItemChangeEmail<R, P>(
        id: number | string,
        entity: P,
        extraParams: PrimitiveObject = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.PATCH_USER_ITEM_CHANGE_EMAIL,
        };
        return this.patchItem(id, entity, extraParams);
    }

    public static async postImpersonateRequest<R, P>(
        entity: P,
        extraParams = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.POST_USER_IMPERSONATE_REQUEST,
        };
        return this.postCollection(entity, extraParams);
    }

    public static async postImport<R, P>(
        entity: P,
        extraParams = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.POST_USER_IMPORT_REQUEST,
        };
        return this.postCollection(entity, extraParams);
    }

    public static async getCollectionCoordinator<R>(
        page = 1,
        extraParams: PrimitiveObject = {},
        cancelToken?: (c: Canceler) => void
    ): Promise<FinalResponse<ListResponse<R> | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.GET_COLLECTION_COORDINATOR,
        };
        return this.getCollection(page, extraParams, cancelToken);
    }

    public static async getCollectionSchedule<R>(
        page = 1,
        extraParams: PrimitiveObject = {},
        cancelToken?: (c: Canceler) => void
    ): Promise<FinalResponse<ListResponse<R> | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.GET_COLLECTION_SCHEDULE,
        };
        return this.getCollection(page, extraParams, cancelToken);
    }

    public static async getCollectionForAssign<R>(
        page = 1,
        extraParams: PrimitiveObject = {},
        cancelToken?: (c: Canceler) => void
    ): Promise<FinalResponse<ListResponse<R> | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.GET_COLLECTION_FOR_ASSIGN,
        };
        return this.getCollection(page, extraParams, cancelToken);
    }

    public static async getCollectionAssign<R>(
        page = 1,
        extraParams: PrimitiveObject = {},
        cancelToken?: (c: Canceler) => void
    ): Promise<FinalResponse<ListResponse<R> | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.GET_COLLECTION_ASSIGN,
        };
        return this.getCollection(page, extraParams, cancelToken);
    }

    public static async getCollectionUserMessage<R>(
        page = 1,
        extraParams = {},
        cancelToken?: (c: Canceler) => void
    ): Promise<FinalResponse<ListResponse<R> | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.GET_COLLECTION_USER_MESSAGE,
        };
        return this.getCollection(page, extraParams, cancelToken);
    }

    public static async getItemIsMessageComplete<R>(
        id: number | string,
        extraParams: PrimitiveObject = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.routeToPath(this.GET_ITEM_USER_MESSAGE_COMPLETE, {
                id,
            }),
        };
        return this.getItem(id, extraParams);
    }

    public static async postDownload<R>(
        id: number | string,
        type: string,
        extraParams = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.routeToPath(this.POST_DOWNLOAD, {
                id,
                type,
            }),
        };
        return this.postCollection({}, extraParams, {
            responseType: "blob",
        });
    }

    public static async getItemStudent<R>(
        id: number | string,
        extraParams: PrimitiveObject = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.routeToPath(this.GET_ITEM_STUDENT, {
                id,
            }),
        };
        return this.getItem(id, extraParams);
    }

    public static async postCollectionExport<R, P>(
        entity: P,
        extraParams = {}
    ): Promise<FinalResponse<R | null>> {
        extraParams = {
            ...extraParams,
            PATH: this.POST_USER_EXPORT_REQUEST,
        };
        return this.postCollection(entity, extraParams, {
            responseType: "blob",
        });
    }
}
